"use client";
import { Skeleton } from "@/app/components/ui/skeleton";
import { generateImageUrlFromKey } from "@/lib/utils";
import { Auction } from "@prisma/client";
import { Flex, Text } from "@radix-ui/themes";
import axios from "axios";
import Image from "next/image";
import Link from "next/link";
import useSWR from "swr";

interface Props {
  auction: Auction;
}

const NO_IMAGES = "NO_IMAGES";

const AuctionRandomImagePreview = ({ auction }: Props) => {
  const fetcher = async (url: string) => {
    const response = await axios.get(url);
    if (response.data.key === NO_IMAGES) return { imageUrl: NO_IMAGES, lotId: null };

    if (response?.data?.key) {
      return {
        imageUrl: generateImageUrlFromKey(response.data.key),
        lotId: response?.data?.lotId ?? null
      };
    }
    return { imageUrl: null, lotId: null };
  };

  const { data, error } = useSWR(
    `/api/image/auctions/${auction.id}/random`,
    fetcher,
  );

  const imageUrl = data?.imageUrl;
  const lotId = data?.lotId;

  if (imageUrl === NO_IMAGES) {
    return (
      <Flex
        className="invisible h-0 md:visible md:w-1/3 md:h-72 border"
        align="center"
        justify="center"
      >
        <Text className="text-xs">No images in this auction.</Text>
      </Flex>
    );
  } else if (imageUrl) {
    return (
      <Flex className="invisible md:visible md:w-1/3" align="end">
        <Link href={lotId ? `/auctions/${auction.id}/lots/${lotId}` : '#'} className="w-full">
          <Flex className="w-full h-0 md:h-80 py-5 px-3 relative overflow-hidden">
            <Image
              src={imageUrl}
              alt="Lot image"
              priority
              unoptimized
              style={{ objectFit: "scale-down" }}
              fill
              className="hover:scale-103"
            />
          </Flex>
        </Link>
      </Flex>
    );
  } else {
    return <Skeleton className="w-1/3 h-72 rounded-none" />;
  }
};

export default AuctionRandomImagePreview;
